import React, { useEffect, useRef, useState } from 'react'
import { Feature } from '../../api/client'
import { ReferenceSelector } from './ReferenceSelector'
import ReferenceValueDisplay from '../ReferenceValueDisplay'
import { resolveCollectionItemName } from '../../utils/collectionUtils'
import LocationPickerModal, {
    LocationPickerInitial,
    LocationPickerResult,
} from './LocationPickerModal'
import BoolToggle from './BoolToggle'
import { boolLabels } from '../../utils/boolFeature'

/**
 * Map feature editor. One row per (key, value) pair; the key and value
 * cells are independently rendered by sub-type, driven by
 * `feature.config.params.key` and `feature.config.params.value`.
 *
 * Apple-style apply-on-change: every mutation (input edit, picker confirm,
 * row add/remove) fires `onChange` immediately with the full row array.
 * Click-outside, Esc, or the header × just dismiss — no batched save.
 *
 * Rows here are intentionally not `CheckinFeature`: they carry only the
 * fields the editor manipulates plus a stable `id` for React keys and
 * `*NameHint` strings used to render a freshly-picked Places/People item
 * before the page-level `collectionItems` cache catches up. The caller
 * (`CheckinComposer.handleMapChange`) maps these rows back into
 * `CheckinFeature`s, doing the live-vs-tombstone diff parallel to the
 * existing list/location handlers.
 */
export interface MapRow {
    /** Stable row id for React keys; persisted across re-render but not
     *  sent to the backend. */
    id: string
    key_freeform_value?: string
    key_decimal_value?: number
    key_reference_value?: number
    decimal_value?: number
    freeform_value?: string
    reference_value?: number
    latitude?: number
    longitude?: number
    /** UX-only fallback display names carried by rows whose Places/People
     *  ref was just picked through a modal. The parent's `collectionItems`
     *  snapshot lags brand-new items created via /find_or_create, so without
     *  the hint we render "Place #N" until the next reload. Stripped at
     *  save time. */
    keyNameHint?: string
    valueNameHint?: string
}

interface SubConfig {
    type?: string
    params?: any
}

interface MapEditorProps {
    feature: Feature
    values: MapRow[]
    collectionItems?: Record<string, any>
    /** Forwarded to `LocationPickerModal` so freshly-created Places land in
     *  the page-level cache. Mirrors `LocationListEditor`'s use. */
    onCollectionItemReady?: (item: { id: number; data: Record<string, any> }) => void
    onChange: (values: MapRow[]) => void
    onClose: () => void
}

const MapEditor: React.FC<MapEditorProps> = ({
    feature,
    values,
    collectionItems,
    onCollectionItemReady,
    onChange,
    onClose,
}) => {
    const keySub: SubConfig = feature.config?.params?.key ?? {}
    const valueSub: SubConfig = feature.config?.params?.value ?? {}
    // A yes/no key has only two answers, and a checkin holds one pair per key.
    const boolKey = keySub.type === 'bool'

    const [rows, setRows] = useState<MapRow[]>(values)
    const containerRef = useRef<HTMLDivElement>(null)
    // Latest snapshot for async callbacks (picker confirm, etc.) that may
    // fire after additional mutations have landed.
    const rowsRef = useRef(rows)
    useEffect(() => {
        rowsRef.current = rows
    }, [rows])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                onClose()
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [onClose])

    const commit = (next: MapRow[]) => {
        setRows(next)
        onChange(next)
    }

    const updateRow = (id: string, patch: Partial<MapRow>) => {
        commit(rowsRef.current.map((r) => (r.id === id ? { ...r, ...patch } : r)))
    }

    const removeRow = (id: string) => {
        commit(rowsRef.current.filter((r) => r.id !== id))
    }

    const addRow = () => {
        commit([
            ...rowsRef.current,
            { id: `pair-${Date.now()}-${Math.random().toString(36).slice(2, 7)}` },
        ])
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            e.preventDefault()
            onClose()
        }
    }

    return (
        <div
            ref={containerRef}
            className="absolute z-50 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 min-w-96 p-3"
            onKeyDown={handleKeyDown}
            tabIndex={-1}
        >
            <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium text-gray-700">
                    Edit {feature.title}
                </div>
                <button
                    onClick={onClose}
                    className="p-1 text-gray-400 hover:text-gray-700 rounded"
                    title="Dismiss"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            <div className="space-y-2 mb-3">
                {rows.map((row) => (
                    <div key={row.id} className="flex items-center gap-2">
                        <MapCell
                            side="key"
                            row={row}
                            sub={keySub}
                            takenKeys={boolKey
                                ? rows
                                    .filter((r) => r.id !== row.id && r.key_decimal_value != null)
                                    .map((r) => Number(r.key_decimal_value))
                                : undefined}
                            collectionItems={collectionItems}
                            onCollectionItemReady={onCollectionItemReady}
                            onPatch={(patch) => updateRow(row.id, patch)}
                        />
                        <span className="text-gray-400 select-none">→</span>
                        <MapCell
                            side="value"
                            row={row}
                            sub={valueSub}
                            collectionItems={collectionItems}
                            onCollectionItemReady={onCollectionItemReady}
                            onPatch={(patch) => updateRow(row.id, patch)}
                        />
                        <button
                            onClick={() => removeRow(row.id)}
                            className="p-1 text-gray-400 hover:text-rose-500 rounded"
                            title="Remove pair"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </button>
                    </div>
                ))}

                {rows.length === 0 && (
                    <div className="text-sm text-gray-500 text-center py-2">
                        No pairs yet
                    </div>
                )}
            </div>

            <button
                onClick={addRow}
                disabled={boolKey && rows.length >= 2}
                className="disabled:opacity-40 disabled:cursor-not-allowed w-full px-3 py-2 text-sm border border-dashed border-gray-300 rounded text-gray-600 hover:border-amber-300 hover:text-amber-900"
            >
                + Add pair
            </button>
        </div>
    )
}

interface MapCellProps {
    side: 'key' | 'value'
    row: MapRow
    sub: SubConfig
    collectionItems?: Record<string, any>
    onCollectionItemReady?: (item: { id: number; data: Record<string, any> }) => void
    onPatch: (patch: Partial<MapRow>) => void
    /** Bool key only: answers other pairs already use. */
    takenKeys?: number[]
}

// Cleanup helpers so patches always leave the row in a single-column state
// for the half they touch — defensive against stale fields from a row that
// somehow carried multiple value-half columns. The feature config locks the
// sub-type after creation (Stage 0), so in practice only one set is ever
// populated.
const KEY_RESET: Partial<MapRow> = {
    key_freeform_value: undefined,
    key_decimal_value: undefined,
    key_reference_value: undefined,
    keyNameHint: undefined,
}
const VALUE_RESET: Partial<MapRow> = {
    freeform_value: undefined,
    decimal_value: undefined,
    reference_value: undefined,
    latitude: undefined,
    longitude: undefined,
    valueNameHint: undefined,
}

const MapCell: React.FC<MapCellProps> = ({
    side,
    row,
    sub,
    collectionItems,
    onCollectionItemReady,
    onPatch,
    takenKeys,
}) => {
    const type = sub?.type
    const params = sub?.params ?? {}
    const [pickerOpen, setPickerOpen] = useState(false)
    const [locPickerOpen, setLocPickerOpen] = useState(false)

    const reset = side === 'key' ? KEY_RESET : VALUE_RESET

    const setStr = (v: string) => {
        if (side === 'key') onPatch({ ...reset, key_freeform_value: v })
        else onPatch({ ...reset, freeform_value: v })
    }
    const setNum = (v: number | undefined) => {
        if (side === 'key') onPatch({ ...reset, key_decimal_value: v })
        else onPatch({ ...reset, decimal_value: v })
    }
    const setRef = (v: number | undefined, hint?: string) => {
        if (side === 'key') onPatch({ ...reset, key_reference_value: v, keyNameHint: hint })
        else onPatch({ ...reset, reference_value: v, valueNameHint: hint })
    }
    const setLoc = (result: LocationPickerResult) => {
        if (result.kind === 'place') {
            onPatch({ ...VALUE_RESET, reference_value: result.collectionItemId, valueNameHint: result.name ?? undefined })
        } else {
            onPatch({ ...VALUE_RESET, latitude: result.latitude, longitude: result.longitude })
        }
    }

    const currentStr = side === 'key' ? row.key_freeform_value : row.freeform_value
    const currentNum = side === 'key' ? row.key_decimal_value : row.decimal_value
    const currentRef = side === 'key' ? row.key_reference_value : row.reference_value
    const currentHint = side === 'key' ? row.keyNameHint : row.valueNameHint

    if (type === 'str') {
        return (
            <input
                type="text"
                value={currentStr ?? ''}
                onChange={(e) => setStr(e.target.value)}
                placeholder={side}
                className="flex-1 min-w-0 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-action"
            />
        )
    }

    if (type === 'num') {
        return (
            <input
                type="number"
                value={currentNum ?? ''}
                onChange={(e) => {
                    const v = e.target.value
                    setNum(v === '' ? undefined : parseFloat(v))
                }}
                placeholder={side}
                className="flex-1 min-w-0 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-action"
            />
        )
    }

    if (type === 'bool') {
        return (
            <div className="flex-1 min-w-0">
                <BoolToggle
                    labels={boolLabels(params)}
                    value={currentNum}
                    onChange={setNum}
                    disabledValues={takenKeys}
                />
            </div>
        )
    }

    if (type === 'enum') {
        return (
            <select
                value={currentStr ?? ''}
                onChange={(e) => setStr(e.target.value)}
                className="flex-1 min-w-0 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-action"
            >
                <option value="">Select…</option>
                {(params.values || []).map((v: string, i: number) => (
                    <option key={i} value={v}>{v}</option>
                ))}
            </select>
        )
    }

    if (type === 'rating' || type === 'labeled_rating') {
        const max = params.max || 5
        const labels: string[] = params.labels || []
        return (
            <select
                value={currentNum ?? ''}
                onChange={(e) => {
                    const v = e.target.value
                    setNum(v === '' ? undefined : parseFloat(v))
                }}
                className="flex-1 min-w-0 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-action"
            >
                <option value="">Select…</option>
                {Array.from({ length: max }, (_, i) => i + 1).map((rating) => {
                    const label = type === 'labeled_rating' && labels[rating - 1]
                        ? `${rating} - ${labels[rating - 1]}`
                        : rating.toString()
                    return <option key={rating} value={rating}>{label}</option>
                })}
            </select>
        )
    }

    if (type === 'reference') {
        const collectionId = params.collection_id
        // Resolve the item label through the same path as the chip
        // (`MapValueDisplay`/`ReferenceValueDisplay`), so collections whose
        // display field isn't literally `name` (e.g. People → `first name`)
        // render their canonical label here too. The hint covers the brief
        // window where a just-picked item isn't in `collectionItems` yet.
        const haveItem = currentRef != null && collectionItems?.[String(currentRef)] != null
        let label: React.ReactNode = null
        if (currentRef != null) {
            if (haveItem) {
                label = (
                    <ReferenceValueDisplay
                        itemId={currentRef}
                        showPrefix={false}
                        collectionItems={collectionItems ?? {}}
                    />
                )
            } else {
                label = currentHint ?? `#${currentRef}`
            }
        }
        return (
            <div className="relative flex-1 min-w-0">
                <button
                    type="button"
                    onClick={() => setPickerOpen(true)}
                    className="w-full text-left px-2 py-1 text-sm border border-gray-300 rounded hover:border-amber-400 truncate"
                >
                    {label ?? <span className="text-gray-400">Pick {side}…</span>}
                </button>
                {pickerOpen && collectionId != null && (
                    <ReferenceSelector
                        collectionId={collectionId}
                        value={currentRef ?? undefined}
                        onChange={(itemId) => setRef(itemId)}
                        onClose={() => setPickerOpen(false)}
                    />
                )}
            </div>
        )
    }

    if (type === 'location') {
        // Stage 0 invariant: location is only allowed on the value side.
        const refVal = row.reference_value
        const lat = row.latitude
        const lng = row.longitude
        const refItem = refVal != null ? collectionItems?.[String(refVal)] : undefined
        const resolvedName = refVal != null
            ? resolveCollectionItemName(refVal, collectionItems ?? {})
            : null
        const label = refVal != null
            ? (resolvedName ?? row.valueNameHint ?? `Place #${refVal}`)
            : (lat != null && lng != null ? `${lat.toFixed(2)}°, ${lng.toFixed(2)}°` : null)
        const initial: LocationPickerInitial | null = refVal != null
            ? {
                kind: 'place',
                collectionItemId: refVal,
                lat: refItem?.data?.lat,
                lng: refItem?.data?.lng,
                name: refItem?.data?.name,
                googlePlaceId: refItem?.data?.google_place_id,
            }
            : (lat != null && lng != null ? { kind: 'coords', latitude: lat, longitude: lng } : null)
        return (
            <div className="relative flex-1 min-w-0">
                <button
                    type="button"
                    onClick={() => setLocPickerOpen(true)}
                    className="w-full text-left px-2 py-1 text-sm border border-gray-300 rounded hover:border-amber-400 truncate"
                >
                    {label ? `📍 ${label}` : <span className="text-gray-400">Pick location…</span>}
                </button>
                <LocationPickerModal
                    open={locPickerOpen}
                    initial={initial}
                    onCollectionItemReady={onCollectionItemReady}
                    onConfirm={(result) => {
                        setLocPickerOpen(false)
                        setLoc(result)
                    }}
                    onClose={() => setLocPickerOpen(false)}
                />
            </div>
        )
    }

    return <div className="flex-1 text-sm text-gray-400 px-2">unsupported ({type ?? '?'})</div>
}

export default MapEditor
